import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Code2,
  Copy,
  Database,
  ExternalLink,
  FileCode2,
  KeyRound,
  Menu,
  Search,
  ShieldCheck,
  Sparkles,
  Terminal,
  Workflow,
  X,
  Zap,
} from "lucide-react";
import { BrandMark } from "@/components/AppShell";
import {
  DOC_ENTRIES,
  copyTextToClipboard,
  docsPagePath,
  docsRawMarkdownPath,
  fetchDocsMarkdown,
  findDocEntry,
  resolveDocsPath,
  type DocEntry,
} from "@/lib/docs";
import {
  extractMarkdownHeadings,
  renderMarkdownToSafeHtml,
} from "@/lib/markdown";
import { ROUTES } from "@/routes";

type GuideGroup = {
  title: string;
  description: string;
  slugs: readonly string[];
};

type CopyState = "idle" | "copied" | "failed";

const GUIDE_GROUPS: readonly GuideGroup[] = [
  {
    title: "Start here",
    description: "Get a first request running",
    slugs: ["getting-started", "javascript-client"],
  },
  {
    title: "Identity",
    description: "Keys, auth, and sessions",
    slugs: [
      "api-keys",
      "auth-v2-popup",
      "authentication",
      "session-management",
      "hosted-auth-sso",
    ],
  },
  {
    title: "Data & runtime",
    description: "Query, compute, and ship safely",
    slugs: ["data-api", "edge-functions", "cors-and-security"],
  },
] as const;

const SORTED_DOCS = [...DOC_ENTRIES].sort((a, b) => a.order - b.order);

function GuideIcon({ slug }: { slug: string }) {
  const iconClass = "h-4 w-4";
  if (slug === "getting-started") return <Sparkles className={iconClass} aria-hidden />;
  if (slug === "javascript-client") return <Code2 className={iconClass} aria-hidden />;
  if (slug === "api-keys") return <KeyRound className={iconClass} aria-hidden />;
  if (slug === "data-api") return <Database className={iconClass} aria-hidden />;
  if (slug === "edge-functions") return <Zap className={iconClass} aria-hidden />;
  if (slug === "cors-and-security") return <ShieldCheck className={iconClass} aria-hidden />;
  if (slug === "hosted-auth-sso") return <Workflow className={iconClass} aria-hidden />;
  return <BookOpen className={iconClass} aria-hidden />;
}

function guideMatches(entry: DocEntry, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return true;
  return `${entry.title} ${entry.summary} ${entry.slug}`
    .toLocaleLowerCase()
    .includes(needle);
}

function GuideCard({ entry, featured = false }: { entry: DocEntry; featured?: boolean }) {
  return (
    <Link
      to={docsPagePath(entry.slug)}
      className={`docs-guide-card${featured ? " docs-guide-card--featured" : ""}`}
    >
      <span className="docs-guide-card__topline">
        <span className="docs-guide-card__icon">
          <GuideIcon slug={entry.slug} />
        </span>
        <span className="docs-guide-card__arrow" aria-hidden>
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </span>
      <span className="docs-guide-card__title">{entry.title}</span>
      <span className="docs-guide-card__summary">{entry.summary}</span>
      <span className="docs-guide-card__link">
        Open guide <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </span>
    </Link>
  );
}

function DocsPage() {
  const location = useLocation();
  const params = useParams();
  const splat = params["*"] ?? "";
  const resolved = resolveDocsPath(
    splat ? `/docs/${splat}` : location.pathname,
  );
  const slug = resolved.slug;
  const entry = slug ? findDocEntry(slug) : undefined;
  const title = entry?.title ?? (slug ? slug : "Developer docs");
  const rawHref = docsRawMarkdownPath(slug || null);

  const [markdown, setMarkdown] = useState<string>("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const [query, setQuery] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setMarkdown("");
    setCopyState("idle");
    void (async () => {
      const result = await fetchDocsMarkdown(slug || null);
      if (cancelled) return;
      if (!result.ok) {
        setLoadError(result.error);
        return;
      }
      setMarkdown(result.markdown);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [slug]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} · TinyBase Docs`;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  useEffect(() => {
    const onShortcut = (event: KeyboardEvent) => {
      const target = event.target;
      const isTyping =
        target instanceof HTMLElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
      if (event.key === "/" && !isTyping) {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
      if (event.key === "Escape" && document.activeElement === searchInputRef.current) {
        setQuery("");
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onShortcut);
    return () => window.removeEventListener("keydown", onShortcut);
  }, []);

  const filteredDocs = useMemo(
    () => SORTED_DOCS.filter((doc) => guideMatches(doc, query)),
    [query],
  );

  const headings = useMemo(
    () =>
      extractMarkdownHeadings(markdown).filter(
        (heading) => heading.level >= 2 && heading.level <= 3,
      ),
    [markdown],
  );

  const onCopy = useCallback(async () => {
    if (!markdown) return;
    const ok = await copyTextToClipboard(markdown);
    setCopyState(ok ? "copied" : "failed");
    window.setTimeout(() => setCopyState("idle"), 1600);
  }, [markdown]);

  const onArticleClick = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const button = target.closest<HTMLButtonElement>("[data-code-copy]");
    if (!button) return;
    const code = button
      .closest<HTMLElement>(".docs-code-block")
      ?.querySelector("pre code")?.textContent;
    if (!code) return;
    const label = button.querySelector("span");
    const originalLabel = label?.textContent ?? "Copy";
    void copyTextToClipboard(code).then((ok) => {
      if (label) label.textContent = ok ? "Copied" : "Failed";
      button.dataset.state = ok ? "copied" : "failed";
      window.setTimeout(() => {
        if (label) label.textContent = originalLabel;
        delete button.dataset.state;
      }, 1600);
    });
  }, []);

  const renderGuideNavigation = (mobile = false) =>
    GUIDE_GROUPS.map((group) => (
      <div className="docs-nav-group" key={group.title}>
        <div className="docs-nav-group__heading">
          <span>{group.title}</span>
          <span className="docs-nav-group__count">
            {group.slugs.length.toString().padStart(2, "0")}
          </span>
        </div>
        <p className="docs-nav-group__description">{group.description}</p>
        <div className="docs-nav-group__links">
          {group.slugs.map((groupSlug) => {
            const guide = DOC_ENTRIES.find((item) => item.slug === groupSlug);
            if (!guide || !guideMatches(guide, query)) return null;
            const active = slug === guide.slug;
            return (
              <Link
                key={guide.slug}
                to={docsPagePath(guide.slug)}
                onClick={mobile ? () => setMobileNavOpen(false) : undefined}
                className={`docs-nav-link${active ? " docs-nav-link--active" : ""}`}
                aria-current={active ? "page" : undefined}
              >
                <span className="docs-nav-link__icon">
                  <GuideIcon slug={guide.slug} />
                </span>
                <span className="docs-nav-link__label">{guide.title}</span>
                {active ? <ChevronRight className="docs-nav-link__chevron" aria-hidden /> : null}
              </Link>
            );
          })}
        </div>
      </div>
    ));

  const currentIndex = SORTED_DOCS.findIndex((doc) => doc.slug === slug);
  const previous = currentIndex > 0 ? SORTED_DOCS[currentIndex - 1] : undefined;
  const next =
    currentIndex >= 0 && currentIndex < SORTED_DOCS.length - 1
      ? SORTED_DOCS[currentIndex + 1]
      : undefined;

  return (
    <div className="docs-page">
      <header className="docs-masthead">
        <div className="docs-masthead__inner">
          <div className="docs-brand-cluster">
            <Link
              to={ROUTES.home}
              className="docs-brand"
              aria-label="TinyBase home"
            >
              <BrandMark className="docs-brand__mark" />
              <span className="docs-brand__name">TinyBase</span>
            </Link>
            <span className="docs-masthead__divider" aria-hidden />
            <span className="docs-masthead__section">Developer docs</span>
          </div>

          <div className="docs-masthead__actions">
            <div className="docs-search-shell">
              <label className="docs-search-box">
                <Search className="docs-search-box__icon" aria-hidden />
                <span className="sr-only">Search developer docs</span>
                <input
                  ref={searchInputRef}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search guides"
                  aria-label="Search developer docs"
                  autoComplete="off"
                />
                {query ? (
                  <button
                    type="button"
                    className="docs-search-box__clear"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : (
                  <kbd className="docs-search-box__shortcut">/</kbd>
                )}
              </label>
              {query.trim() ? (
                <div className="docs-search-results" role="listbox" aria-label="Search results">
                  <div className="docs-search-results__label">
                    {filteredDocs.length} {filteredDocs.length === 1 ? "guide" : "guides"} found
                  </div>
                  {filteredDocs.slice(0, 6).map((doc) => (
                    <Link
                      key={doc.slug}
                      to={docsPagePath(doc.slug)}
                      className="docs-search-result"
                      onClick={() => setQuery("")}
                      role="option"
                    >
                      <span className="docs-search-result__icon"><GuideIcon slug={doc.slug} /></span>
                      <span>
                        <strong>{doc.title}</strong>
                        <small>{doc.summary}</small>
                      </span>
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </Link>
                  ))}
                  {!filteredDocs.length ? (
                    <p className="docs-search-results__empty">No guide matches “{query}”. Try “auth” or “data”.</p>
                  ) : null}
                </div>
              ) : null}
            </div>
            <Link to={ROUTES.home} className="docs-masthead__link docs-masthead__link--home">
              Home
            </Link>
            <Link to={ROUTES.login} className="docs-masthead__link">
              Sign in
            </Link>
            <Link to={ROUTES.dashboard} className="docs-dashboard-link">
              Open dashboard <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </div>
        </div>
      </header>

      <div className="docs-mobile-toolbar">
        <button
          type="button"
          className="docs-mobile-toolbar__button"
          onClick={() => setMobileNavOpen((open) => !open)}
          aria-expanded={mobileNavOpen}
          aria-controls="docs-mobile-nav"
        >
          <Menu className="h-4 w-4" aria-hidden />
          Guides
          <ChevronDown className={`h-4 w-4 transition-transform${mobileNavOpen ? " rotate-180" : ""}`} aria-hidden />
        </button>
        <span className="docs-mobile-toolbar__meta">{SORTED_DOCS.length} integration guides</span>
      </div>

      {mobileNavOpen ? (
        <div id="docs-mobile-nav" className="docs-mobile-nav">
          <div className="docs-mobile-nav__header">
            <span>Browse guides</span>
            <button type="button" onClick={() => setMobileNavOpen(false)} aria-label="Close guide navigation">
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          {renderGuideNavigation(true)}
        </div>
      ) : null}

      <div className="docs-layout">
        <aside className="docs-sidebar" aria-label="Developer documentation">
          <div className="docs-sidebar__intro">
            <span className="docs-sidebar__eyebrow">Build with TinyBase</span>
            <p>One API surface for auth, data, and serverless runtime.</p>
          </div>
          <Link
            to={ROUTES.docs}
            className={`docs-sidebar__overview${!slug ? " docs-sidebar__overview--active" : ""}`}
            aria-current={!slug ? "page" : undefined}
          >
            <span className="docs-sidebar__overview-icon"><Terminal className="h-4 w-4" aria-hidden /></span>
            <span>
              <strong>Overview</strong>
              <small>Choose your path</small>
            </span>
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
          <div className="docs-sidebar__nav">{renderGuideNavigation()}</div>
          <div className="docs-sidebar__footer">
            <span className="docs-status-dot" aria-hidden />
            <span>API operational</span>
            <a href="https://api.knotree.dev/v1/health" target="_blank" rel="noreferrer" aria-label="Open API health endpoint">
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          </div>
        </aside>

        <main className="docs-main">
          {!slug ? (
            <DocsCatalog
              docs={filteredDocs}
              total={SORTED_DOCS.length}
              markdown={markdown}
              rawHref={rawHref}
              copyState={copyState}
              onCopy={() => void onCopy()}
            />
          ) : (
            <div className="docs-article-page">
              <div className="docs-breadcrumbs" aria-label="Breadcrumb">
                <Link to={ROUTES.docs}>Docs</Link>
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                <span>{entry ? "Guide" : "Reference"}</span>
              </div>

              <header className="docs-article-heading">
                <div className="docs-article-heading__eyebrow">
                  <span className="docs-pill"><BookOpen className="h-3.5 w-3.5" aria-hidden /> Guide</span>
                  {entry ? <span>{entry.slug}</span> : null}
                </div>
                <h1>{title}</h1>
                {entry?.summary ? <p>{entry.summary}</p> : null}
                <div className="docs-article-actions">
                  <button
                    type="button"
                    className="docs-action docs-action--primary"
                    onClick={() => void onCopy()}
                    disabled={!markdown}
                    data-testid="docs-copy-markdown"
                  >
                    {copyState === "copied" ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
                    {copyState === "copied" ? "Copied" : copyState === "failed" ? "Copy failed" : "Copy markdown"}
                  </button>
                  <a href={rawHref} className="docs-action" data-testid="docs-raw-link">
                    <ExternalLink className="h-4 w-4" aria-hidden />
                    Raw markdown
                  </a>
                  <span className="docs-article-actions__hint">Press <kbd>/</kbd> to search</span>
                </div>
              </header>

              {loadError ? (
                <div className="docs-load-state docs-load-state--error" role="alert">
                  <strong>Could not load this guide.</strong>
                  <span>{loadError}</span>
                </div>
              ) : !markdown ? (
                <div className="docs-load-state" data-testid="docs-markdown-loading">
                  <span className="docs-loading-line docs-loading-line--wide" />
                  <span className="docs-loading-line" />
                  <span className="docs-loading-line docs-loading-line--short" />
                  <span className="docs-loading-label">Loading guide…</span>
                </div>
              ) : (
                <article
                  className="docs-prose docs-prose--guide"
                  data-testid="docs-markdown-body"
                  onClick={onArticleClick}
                  // Safe HTML from renderMarkdownToSafeHtml (escapes raw HTML; limited tags).
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdownToSafeHtml(markdown),
                  }}
                />
              )}

              {markdown && !loadError ? (
                <nav className="docs-pagination" aria-label="Guide navigation">
                  {previous ? (
                    <Link to={docsPagePath(previous.slug)} className="docs-pagination__link docs-pagination__link--previous">
                      <span><small>Previous guide</small><strong>{previous.title}</strong></span>
                      <ArrowLeft className="h-4 w-4" aria-hidden />
                    </Link>
                  ) : <span />}
                  {next ? (
                    <Link to={docsPagePath(next.slug)} className="docs-pagination__link docs-pagination__link--next">
                      <span><small>Next guide</small><strong>{next.title}</strong></span>
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </Link>
                  ) : null}
                </nav>
              ) : null}
            </div>
          )}
        </main>

        <aside className="docs-toc" aria-label="On this page">
          {slug && headings.length ? (
            <>
              <p className="docs-toc__label">On this page</p>
              <nav className="docs-toc__links">
                {headings.map((heading) => (
                  <a
                    key={`${heading.id}-${heading.level}`}
                    href={`#${heading.id}`}
                    className={`docs-toc__link${heading.level === 3 ? " docs-toc__link--nested" : ""}`}
                  >
                    {heading.text}
                  </a>
                ))}
              </nav>
              <div className="docs-toc__rule" />
              <a href={rawHref} className="docs-toc__raw-link">
                <FileCode2 className="h-3.5 w-3.5" aria-hidden />
                View raw source
              </a>
            </>
          ) : (
            <div className="docs-toc__catalog">
              <span className="docs-toc__label">Quick route</span>
              <strong>From first call to production.</strong>
              <p>Start with the client, add identity, then move into data and runtime guides.</p>
              <Link to={docsPagePath("getting-started")}>
                Start building <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </div>
          )}
        </aside>
      </div>

      <footer className="docs-footer">
        <div>
          <span className="docs-footer__brand">TinyBase</span>
          <span>Developer docs for the product layer.</span>
        </div>
        <div>
          <span>Raw markdown is always available at</span>
          <a href={rawHref}>{rawHref}</a>
        </div>
      </footer>
    </div>
  );
}

function DocsCatalog({
  docs,
  total,
  markdown,
  rawHref,
  copyState,
  onCopy,
}: {
  docs: readonly DocEntry[];
  total: number;
  markdown: string;
  rawHref: string;
  copyState: CopyState;
  onCopy: () => void;
}) {
  const featured = docs.filter((doc) =>
    ["getting-started", "javascript-client", "data-api"].includes(doc.slug),
  );
  const catalogDocs = docs.filter(
    (doc) => !featured.some((featuredDoc) => featuredDoc.slug === doc.slug),
  );

  return (
    <div className="docs-catalog" data-testid="docs-catalog">
      <section className="docs-hero">
        <div className="docs-hero__copy">
          <div className="docs-eyebrow">
            <span className="docs-live-dot" aria-hidden />
            catalog / {total} integration guides
          </div>
          <h1>Build the product. Keep the backend quiet.</h1>
          <p>
            A practical path from your first client call to production-ready auth,
            data access, and edge runtime. Use the guide that matches the next
            thing you need to ship.
          </p>
          <div className="docs-hero__actions">
            <Link to={docsPagePath("getting-started")} className="docs-hero__primary">
              Start building <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <a href="https://api.knotree.dev/v1/health" target="_blank" rel="noreferrer" className="docs-hero__secondary">
              Check API status <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          </div>
          <div className="docs-catalog__source-actions">
            <button
              type="button"
              className="docs-catalog__source-button"
              onClick={onCopy}
              disabled={!markdown}
              data-testid="docs-copy-markdown"
            >
              {copyState === "copied" ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
              {copyState === "copied" ? "Copied" : copyState === "failed" ? "Copy failed" : "Copy markdown"}
            </button>
            <a href={rawHref} className="docs-catalog__source-link" data-testid="docs-raw-link">
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              Raw source
            </a>
          </div>
          <div className="docs-hero__facts" aria-label="TinyBase capabilities">
            <span><i aria-hidden /> TypeScript-first</span>
            <span><i aria-hidden /> App-user auth</span>
            <span><i aria-hidden /> RLS-ready data</span>
          </div>
        </div>
        <div className="docs-hero__console" aria-label="TinyBase first request example">
          <div className="docs-console__topbar">
            <span className="docs-console__dots" aria-hidden><i /><i /><i /></span>
            <span className="docs-console__filename">first-request.ts</span>
            <span className="docs-console__status"><span aria-hidden /> ready</span>
          </div>
          <div className="docs-console__body">
            <div><span>01</span><code><b>import</b> {"{ createClient }"} <b>from</b> <em>"@knotree/client"</em>;</code></div>
            <div><span>02</span><code>&nbsp;</code></div>
            <div><span>03</span><code><b>const</b> tinybase = createClient({"{"}</code></div>
            <div><span>04</span><code>&nbsp;&nbsp;projectKey: <em>"tb_pk_your_key"</em>,</code></div>
            <div><span>05</span><code>{"}"});</code></div>
            <div><span>06</span><code>&nbsp;</code></div>
            <div><span>07</span><code><b>const</b> {"{ data }"} = <b>await</b> tinybase</code></div>
            <div><span>08</span><code>&nbsp;&nbsp;.from(<em>"todos"</em>).select(<em>"*"</em>);</code></div>
          </div>
          <div className="docs-console__footer">
            <Terminal className="h-3.5 w-3.5" aria-hidden />
            <span>authenticated request path</span>
            <span className="docs-console__latency">200 OK · 84ms</span>
          </div>
        </div>
      </section>

      <section className="docs-catalog-section">
        <div className="docs-section-heading">
          <div>
            <span className="docs-section-heading__eyebrow">The shortest path</span>
            <h2>Start with the essentials</h2>
          </div>
          <p>Three small steps that get a useful product loop running.</p>
        </div>
        <div className="docs-feature-grid">
          {featured.map((doc) => <GuideCard key={doc.slug} entry={doc} featured />)}
        </div>
      </section>

      <section className="docs-catalog-section docs-catalog-section--all">
        <div className="docs-section-heading">
          <div>
            <span className="docs-section-heading__eyebrow">Reference map</span>
            <h2>Everything you need to ship</h2>
          </div>
          <p>Keep this map open while you wire each product surface.</p>
        </div>
        <div className="docs-guide-grid">
          {catalogDocs.map((doc) => <GuideCard key={doc.slug} entry={doc} />)}
        </div>
      </section>
    </div>
  );
}

export { DocsPage };
